import {attr, controller, target} from '@github/catalyst'
import {isLanguageSupported, translate, triggerLanguageDetection} from './translations-api'
import {availableTranslations} from './translations-config'
import {sendEvent} from '@github-ui/hydro-analytics'

@controller
class InlineMachineTranslationElement extends HTMLElement {
  @target button: HTMLButtonElement
  @target triggerContainer: HTMLElement
  @target fromLanguageContainer: HTMLSpanElement
  @target targetLanguageContainer: HTMLSpanElement

  private originalContent: string
  private originalContentLength: number
  private isReply: boolean
  private isNestedReply: boolean
  private showsTranslatedContent = false
  private observer?: IntersectionObserver

  static attrPrefix = ''
  @attr detectedLanguage = ''
  @attr detectLanguageUrl = ''
  // Remember if we've already triggered language detection for this comment to prevent duplicate requests
  private triggeredLanguageDetection = false

  private async updateUserInterface() {
    const localTranslation = availableTranslations[this.getBrowserLanguage()]
    const detectedLanguage = await this.detectLanguageIfNeeded()

    if (!localTranslation) {
      this.triggerContainer.hidden = true
      return
    }

    const translateToText = localTranslation.translateTo
    const translatedToText = localTranslation.viewOriginal
    const originalContentText = localTranslation.originalContent[detectedLanguage]

    if (originalContentText) {
      this.fromLanguageContainer.textContent = originalContentText
      this.targetLanguageContainer.textContent = this.showsTranslatedContent ? translatedToText : translateToText
    }
  }

  connectedCallback() {
    this.observer = new IntersectionObserver(this.observerCallback.bind(this), {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    })

    if (this.triggerContainer.parentElement) {
      // We observe the parent, because the button itself is hidden by default and does not trigger any observation
      this.observer.observe(this.triggerContainer.parentElement)
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect()
  }

  private async observerCallback(entries: IntersectionObserverEntry[]) {
    if (entries[0]?.intersectionRatio === 1) {
      const detectedLanguage = await this.detectLanguageIfNeeded()
      const browserLanguage = this.getBrowserLanguage()
      const shouldOfferTranslation = isLanguageSupported(detectedLanguage) && browserLanguage !== detectedLanguage
      this.triggerContainer.hidden = !shouldOfferTranslation
      this.trackTelemetry(shouldOfferTranslation ? 'UGC_translation_offered' : 'UGC_translation_not_offered', {
        language_from: this.detectedLanguage ?? '',
        language_to: this.getBrowserLanguage(),
      })
      await this.updateUserInterface()
    }
  }

  private trackTelemetry(event: string, context: Record<string, string | number | boolean | null | undefined>) {
    sendEvent(event, context)
  }

  private async detectLanguageIfNeeded(): Promise<string> {
    const sourceText = this.getSourceElement()?.outerHTML
    if (!sourceText) {
      return Promise.resolve('')
    }
    if (!this.detectedLanguage && !this.triggeredLanguageDetection && this.detectLanguageUrl) {
      this.triggeredLanguageDetection = true
      await triggerLanguageDetection(this.detectLanguageUrl)
    }

    return Promise.resolve(this.detectedLanguage)
  }

  private setShowsTranslatedContent(showsTranslatedContent: boolean): void {
    // Telemetry
    this.trackTelemetry(showsTranslatedContent ? 'UGC_translation_enabled' : 'UGC_translation_disabled', {
      original_content_length: this.originalContentLength,
      is_reply: this.isReply,
      is_nested_reply: this.isNestedReply,
      language_from: this.detectedLanguage ?? '',
      language_to: this.getBrowserLanguage(),
    })

    this.showsTranslatedContent = showsTranslatedContent
    this.updateUserInterface()
  }

  private getBrowserLanguage(): string {
    return navigator.language.slice(0, 2).toLowerCase()
  }

  private toggleFeedback(targetLanguage: string): void {
    const getFeedbackElement = this.getFeedbackElement()

    if (!getFeedbackElement) {
      return
    }

    // Toggle visibility
    getFeedbackElement.hidden = !getFeedbackElement.hidden

    const wasThisTranslationHelpful = getFeedbackElement.querySelector<HTMLSpanElement>(
      '.js-was-this-translation-helpful',
    )
    const giveFeedback = getFeedbackElement.querySelector<HTMLAnchorElement>('.js-give-feedback')

    if (!wasThisTranslationHelpful || !giveFeedback) {
      return
    }

    // Update "Was this translation helpful?" text
    wasThisTranslationHelpful.innerHTML = availableTranslations[targetLanguage]!.wasThisTranslationHelpful
    // Update "Give feedback" text and link
    giveFeedback.innerHTML = availableTranslations[targetLanguage]!.giveFeedback
    giveFeedback.href = availableTranslations[targetLanguage]!.translationFeedbackLink
  }

  override async click(): Promise<void> {
    const sourceElement = this.getSourceElement()
    if (!sourceElement) {
      return
    }

    const targetLanguage = this.getBrowserLanguage()
    if (!isLanguageSupported(targetLanguage)) {
      return
    }

    this.toggleFeedback(targetLanguage)

    if (this.showsTranslatedContent) {
      sourceElement.innerHTML = this.originalContent
      this.setShowsTranslatedContent(false)
      return
    }

    const detectedLanguage = await this.detectLanguageIfNeeded()

    if (!isLanguageSupported(detectedLanguage)) {
      return
    }

    const type = sourceElement.getAttribute('data-target-translation-type') || ''
    const id = sourceElement.getAttribute('data-target-translation-id') || ''

    const translatedContentResponse = await translate({
      id,
      type,
      from: detectedLanguage,
      to: targetLanguage,
    })

    const translatedContent = translatedContentResponse[0].translations[0].text
    this.originalContent = sourceElement.innerHTML
    this.originalContentLength = sourceElement.querySelector('td')?.innerHTML.trim().length ?? 0
    this.isReply = type === 'comment'
    this.isNestedReply = sourceElement.closest('.nested-discussion-timeline-comment') !== null
    sourceElement.innerHTML = translatedContent

    this.setShowsTranslatedContent(true)
  }

  private getSourceElement(): HTMLTableSectionElement | undefined | null {
    const commentContainer = this.button.closest<HTMLElement>('.timeline-comment')
    return commentContainer?.querySelector<HTMLTableSectionElement>('.js-translation-source')
  }

  private getFeedbackElement(): HTMLParagraphElement | undefined | null {
    const commentContainer = this.button.closest<HTMLElement>('.timeline-comment')
    return commentContainer?.querySelector<HTMLParagraphElement>('.js-translation-feedback')
  }
}
